import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Star } from "lucide-react";
import Reveal from "../components/site/Reveal";
import TrustBar from "../components/site/TrustBar";
import FeaturedServices from "../components/site/sections/FeaturedServices";
import WhyChooseUs from "../components/site/sections/WhyChooseUs";
import MeetTyler from "../components/site/sections/MeetTyler";
import BeforeAfterSlider from "../components/site/BeforeAfterSlider";
import FloorVisualizer from "../components/site/FloorVisualizer";
import DesignCarousel from "../components/site/DesignCarousel";
import ProcessSteps from "../components/site/sections/ProcessSteps";
import GalleryGrid from "../components/site/sections/GalleryGrid";
import Reviews from "../components/site/sections/Reviews";
import ServiceAreas from "../components/site/sections/ServiceAreas";
import LeadForm from "../components/site/LeadForm";
import ClickToPlayVideo from "../components/site/ClickToPlayVideo";
import Seo, { localBusinessSchema } from "../components/site/Seo";
import { IMG, PHONE_DISPLAY, PHONE_TEL, REVIEW_RATING, REVIEW_COUNT } from "../lib/site";
import { useGoogleReviews } from "../lib/useGoogleReviews";
import { HOME } from "@/constants/testIds";

const Home = () => {
  const g = useGoogleReviews();
  const rating = (g.configured && g.rating ? g.rating : REVIEW_RATING).toFixed(1);
  const count = g.configured && g.total ? g.total : REVIEW_COUNT;
  const reviewLine = <><strong>{rating} Stars</strong> on Google • {count} Reviews • Trusted Local Expert</>;
  return (
  <div>
    <Seo
      title="Garage Floor Coatings in Prescott, Flagstaff & Sedona | PRYME EPOXY"
      description="PRYME EPOXY installs premium polyaspartic and epoxy garage floor coatings across Northern Arizona. Licensed, bonded, insured. 5.0 stars, lifetime warranty, free estimates."
      path="/"
      jsonLd={localBusinessSchema()}
    />
    {/* 1. HERO */}
    <section className="relative min-h-[88vh] flex items-center bg-black overflow-hidden py-16">
      <div className="absolute inset-x-0 top-0 h-[650px] lg:h-full z-0 overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="/images/hero-mobile-poster.jpg"
          className="lg:hidden w-full h-full object-cover object-top opacity-90"
        >
          <source src="/videos/hero-mobile.mp4" type="video/mp4" />
        </video>
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="/images/hero-poster.jpg"
          className="hidden lg:block w-full h-full object-cover object-center opacity-90"
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/25 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8 flex flex-col items-start space-y-6 text-left mt-[400px] lg:mt-0">
          <Reveal>
            <div className="hidden lg:flex flex-wrap gap-2">
              <span className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs tracking-widest font-semibold uppercase px-3 py-1.5">
                Licensed • Bonded • Insured
              </span>
              <span className="bg-[#C69453]/15 border border-[#C69453]/40 text-[#C69453] text-xs tracking-widest font-semibold uppercase px-3 py-1.5">
                Residential Lifetime Warranty
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-tight">
              Premium Garage Floor Coatings
              <br />
              <span className="text-[#C69453] font-semibold">Built to Last a Lifetime.</span>
            </h1>
          </Reveal>

          <Reveal delay={0.16}>
            <p className="text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              At PRYME EPOXY we believe inviting someone onto your property is an act of trust. We never cut corners —
              just premium industrial-grade floors installed correctly the first time, right here in Northern Arizona.
            </p>
          </Reveal>

          <Reveal delay={0.24}>
            <div className="flex items-center space-x-3 py-2 border-y border-zinc-800/80 w-full max-w-lg">
              <div className="flex items-center text-yellow-400">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
              </div>
              <span className="text-zinc-200 text-base font-medium">
                {reviewLine}
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.28}>
            <div className="flex lg:hidden flex-wrap gap-2 justify-center mt-6">
              <span className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs tracking-widest font-semibold uppercase px-3 py-1.5">
                Licensed • Bonded • Insured
              </span>
              <span className="bg-[#C69453]/15 border border-[#C69453]/40 text-[#C69453] text-xs tracking-widest font-semibold uppercase px-3 py-1.5">
                Residential Lifetime Warranty
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.32}>
            <div className="hidden lg:flex flex-col sm:flex-row gap-4 w-full sm:w-auto pt-2">
              <Link
                to="/contact"
                data-testid={HOME.getEstimateButton}
                data-track="cta_estimate"
                data-track-label="hero"
                className="bg-[#C69453] hover:bg-[#AE7F44] text-black font-extrabold text-lg px-8 py-4 transition-colors flex items-center justify-center gap-2"
              >
                <span>Get a Free Estimate</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={PHONE_TEL}
                data-testid={HOME.phoneCallButton}
                className="border border-[#C69453] text-[#C69453] hover:bg-[#C69453]/10 font-bold text-lg px-8 py-4 transition-all flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                <span>Call Pryme: {PHONE_DISPLAY}</span>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-4 flex flex-col">
          <Reveal delay={0.2} className="lg:order-2 lg:mt-6">
            <div className="bg-zinc-950/90 backdrop-blur border border-zinc-800 p-8 relative">
              <div className="absolute top-0 right-8 -translate-y-1/2 bg-[#C69453] text-black text-xs uppercase tracking-widest font-black px-3 py-1">
                Owner Operated
              </div>
              <p className="italic text-zinc-300 text-base leading-relaxed">
                &ldquo;When you call PRYME EPOXY, you're dealing directly with me. I prepare every slab with precision
                and make sure your investment is flawless.&rdquo;
              </p>
              <div className="flex items-center space-x-4 border-t border-zinc-900 pt-4 mt-5">
                <div className="bg-zinc-800 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg border border-[#C69453]">
                  T
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white">Tyler</h4>
                  <p className="text-[#C69453] text-xs uppercase tracking-widest font-semibold">Founder &amp; Head Installer</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.28} className="lg:order-1">
            <ClickToPlayVideo
              src="/videos/tyler-message.mp4"
              poster="/images/tyler-message-poster.jpg"
              label="A message from Tyler"
              className="mt-6 lg:mt-0 aspect-[9/16] max-h-[360px] lg:max-h-[520px] mx-auto lg:mx-0"
            />
          </Reveal>
        </div>
      </div>
    </section>

    {/* 2. SOCIAL PROOF */}
    <TrustBar />

    {/* 3. MEET THE FAMILY */}
    <MeetTyler />

    {/* 4. FEATURED SERVICES */}
    <FeaturedServices />

    {/* 5. WHY CHOOSE US */}
    <WhyChooseUs />

    {/* 6. BEFORE & AFTER */}
    <section id="transformation" className="py-20 md:py-24 bg-[#0A0A0A] scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 text-left space-y-5">
          <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">The Transformation</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight">From Tired Concrete to a Floor You'll Love</h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
            Drag the slider to see the difference proper preparation and premium materials make. This is what
            &ldquo;never cutting corners&rdquo; actually looks like.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#C69453] hover:bg-[#AE7F44] text-black font-bold px-6 py-3.5 transition-colors">
            <span>Get Your Free On-Site Estimate</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <div className="lg:col-span-7">
          <BeforeAfterSlider />
        </div>
      </div>
    </section>

    {/* 7. FLOOR VISUALIZER */}
    <section className="py-20 md:py-24 bg-zinc-950 border-y border-zinc-900">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">Design Your Floor</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mt-1">Floor Color &amp; Style Visualizer</h2>
          <p className="text-zinc-400 text-base md:text-lg mt-3">
            Tap a finish to picture how your new floor will look. Then call Tyler to make it real.
          </p>
        </div>
        <FloorVisualizer />
      </div>
    </section>

    {/* SELECT YOUR DESIGN */}
    <DesignCarousel />

    {/* 8. PROCESS */}
    <ProcessSteps />

    {/* 9. GALLERY */}
    <GalleryGrid />

    {/* 10. REVIEWS */}
    <Reviews limit={6} />

    {/* 11. SERVICE AREAS */}
    <ServiceAreas />

    {/* 12. FINAL CTA / LEAD FORM */}
    <LeadForm />
  </div>
  );
};

export default Home;
